/**
 * The `?boot-report` probe — the part that instruments the GPU, split from the
 * part that formats what it saw (`boot-report.ts`) at the 400-line ceiling.
 *
 * Everything here is **off unless the URL asked**: `installBootProbe` returns
 * immediately without the parameter, so an ordinary boot pays nothing for a
 * diagnostic it did not request. What it wraps, and why each wrap is the least
 * invasive one available, is argued at each function; the report's own module
 * doc explains what the numbers mean and why the `gpu errors` line exists.
 */

import { bootReportWanted, type PipelineProbe } from "./boot-report.ts";

interface ProbeState {
  /** One `performance.now()` per created pipeline, so the precompile window can be counted rather than assumed. */
  readonly startedAt: number[];
  asyncCalls: number;
  totalRoundTripMs: number;
  maxRoundTripMs: number;
  resolved: number;
  gpuErrors: number;
  firstGpuError: string;
}

/**
 * How much of a GPU error message the panel keeps.
 *
 * A WGSL validation failure arrives with the whole shader quoted at it, and the
 * panel is a corner of a flight the player is still in. One line is enough to
 * name the thing; the console has the rest, because the probe logs every error
 * it counts.
 */
export const GPU_ERROR_MESSAGE_CHARS = 160;

/** Tags an error with where it surfaced and cuts it to one line — see `GPU_ERROR_MESSAGE_CHARS`. */
export function gpuErrorSummary(source: "pipeline" | "uncaptured", message: string): string {
  const oneLine = message.replace(/\s+/g, " ").trim();
  const cut = oneLine.length > GPU_ERROR_MESSAGE_CHARS ? `${oneLine.slice(0, GPU_ERROR_MESSAGE_CHARS)}…` : oneLine;
  return `[${source}] ${cut || "no message"}`;
}

let probe: ProbeState | undefined;

/**
 * Wraps `GPUDevice.createRenderPipeline`/`Async` so the report can name the two
 * factors of the precompile. A no-op unless the URL asked, and a no-op twice —
 * a second install would double-count every pipeline.
 *
 * `search` is passed in rather than read from `location` for this module's own
 * testability rule; `scene-boot.ts` is the one caller and it has the location.
 */
export function installBootProbe(search: string | undefined): void {
  if (!bootReportWanted(search) || probe !== undefined) return;
  const device = (globalThis as { GPUDevice?: { prototype: Record<string, unknown> } }).GPUDevice;
  const state: ProbeState = {
    startedAt: [],
    asyncCalls: 0,
    totalRoundTripMs: 0,
    maxRoundTripMs: 0,
    resolved: 0,
    gpuErrors: 0,
    firstGpuError: "",
  };
  probe = state;
  const record = (source: "pipeline" | "uncaptured", message: string): void => {
    const summary = gpuErrorSummary(source, message);
    state.gpuErrors += 1;
    if (!state.firstGpuError) state.firstGpuError = summary;
    // Logged as well as counted: the panel has room for one line and a WGSL
    // validation failure is worth its whole text to whoever reads the console.
    console.error(`boot report: GPU error ${state.gpuErrors} — ${summary}`);
  };
  if (!device) return;
  for (const name of ["createRenderPipeline", "createRenderPipelineAsync"] as const) {
    const original = device.prototype[name];
    if (typeof original !== "function") continue;
    const wrapped = original as (this: unknown, descriptor: unknown) => unknown;
    device.prototype[name] = function (this: unknown, descriptor: unknown): unknown {
      const startedAt = performance.now();
      state.startedAt.push(startedAt);
      const made = wrapped.call(this, descriptor);
      if (name === "createRenderPipelineAsync" && made instanceof Promise) {
        state.asyncCalls += 1;
        // `then` with both handlers rather than `.then().catch()`: three awaits
        // this same promise and handles its own rejection, so a `catch` chained
        // here would be a *second* consumer whose own rejection nobody handles.
        void made.then(
          () => {
            const elapsed = performance.now() - startedAt;
            state.totalRoundTripMs += elapsed;
            state.maxRoundTripMs = Math.max(state.maxRoundTripMs, elapsed);
            state.resolved += 1;
          },
          (error: unknown) => record("pipeline", error instanceof Error ? error.message : String(error)),
        );
      }
      return made;
    };
  }
  installErrorScopeProbe(device, record);
  installUncapturedErrorProbe(record);
}

/**
 * Counts three's own pipeline validation failures, by watching what its error
 * scopes come back with.
 *
 * three wraps every `createRenderPipeline`/`Async` in
 * `pushErrorScope('validation')` (`WebGPUPipelineUtils.js`) and reports a
 * non-null result as a `console.error` — which is the whole of the surfacing
 * this port had. Wrapping `popErrorScope` reads the same verdict without
 * touching the renderer's behaviour: the original promise is returned unchanged
 * and three still sees exactly what it saw.
 */
function installErrorScopeProbe(
  device: { prototype: Record<string, unknown> },
  record: (source: "pipeline" | "uncaptured", message: string) => void,
): void {
  const original = device.prototype["popErrorScope"];
  if (typeof original !== "function") return;
  const wrapped = original as (this: unknown) => Promise<{ message?: string } | null>;
  device.prototype["popErrorScope"] = function (this: unknown): Promise<{ message?: string } | null> {
    const popped = wrapped.call(this);
    void popped.then((error) => {
      if (error) record("pipeline", error.message ?? "");
    }, () => undefined);
    return popped;
  };
}

/**
 * Attaches an `uncapturederror` listener to every device this document creates,
 * by wrapping `GPUAdapter.requestDevice` — the probe never gets the device
 * handed to it, because three requests its own inside `WebGPUBackend.init()`.
 *
 * `addEventListener`, never `device.onuncapturederror =`: three assigns that
 * property (`WebGPUBackend.js`) to route errors into `renderer.onError`, and a
 * diagnostic that silenced the renderer's own error path would be a net loss.
 */
function installUncapturedErrorProbe(record: (source: "pipeline" | "uncaptured", message: string) => void): void {
  const adapter = (globalThis as { GPUAdapter?: { prototype: Record<string, unknown> } }).GPUAdapter;
  const original = adapter?.prototype["requestDevice"];
  if (!adapter || typeof original !== "function") return;
  const wrapped = original as (this: unknown, descriptor?: unknown) => Promise<unknown>;
  adapter.prototype["requestDevice"] = function (this: unknown, descriptor?: unknown): Promise<unknown> {
    return wrapped.call(this, descriptor).then((made) => {
      const target = made as { addEventListener?: (type: string, listener: (event: unknown) => void) => void };
      target?.addEventListener?.("uncapturederror", (event: unknown) => {
        const gpuError = (event as { error?: { message?: string } }).error;
        record("uncaptured", gpuError?.message ?? "");
      });
      return made;
    });
  };
}

/**
 * What the probe has seen so far, or zeroes when it was never installed.
 *
 * `marks` bounds the precompile window. Given none — or a boot that never
 * reached `ready` — every pipeline counts, which is the conservative reading:
 * a report from a boot that is still going should not pretend to know which
 * phase its pipelines belonged to.
 */
export function pipelineProbe(marks?: ReadonlyMap<string, number>): PipelineProbe {
  const state = probe;
  if (!state)
    return {
      pipelines: 0,
      asyncCalls: 0,
      roundTripMs: 0,
      maxRoundTripMs: 0,
      inPrecompile: 0,
      gpuErrors: 0,
      firstGpuError: "",
    };
  const from = marks?.get("precompile");
  const to = marks?.get("ready");
  const windowed =
    from === undefined || to === undefined
      ? state.startedAt.length
      : state.startedAt.filter((at) => at >= from && at <= to).length;
  return {
    pipelines: state.startedAt.length,
    asyncCalls: state.asyncCalls,
    roundTripMs: state.resolved > 0 ? state.totalRoundTripMs / state.resolved : 0,
    maxRoundTripMs: state.maxRoundTripMs,
    inPrecompile: windowed,
    gpuErrors: state.gpuErrors,
    firstGpuError: state.firstGpuError,
  };
}
